import axios from 'axios'
import { VueAxios } from './axios'
import { webAuth, expireSession } from './web-auth'
import { ownApi, ambiguousSessionResponse, validProfile } from './web-auth-rules'

// 创建 axios 实例
const request = axios.create({
  // API 请求的默认前缀
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 30000 // 请求超时时间
})

// Only our same-origin API participates; third-party video credentials are independent.
let profileCheck = null
let profileRevision = -1
export function checkWebSession() {
  const revision = webAuth.revision
  if (!profileCheck || profileRevision !== revision) {
    profileRevision = revision
    // The legacy server redirects expired sessions across origins. A manual redirect
    // on this dedicated identity endpoint distinguishes expiry from a CORS/network error.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 15000)
    profileCheck = fetch('/api/userInfo/findLoginMan', {
      credentials: 'same-origin', redirect: 'manual', cache: 'no-store', signal: controller.signal
    }).then(async response => {
      const expired = () => Object.assign(new Error('登录已失效，请重新登录'), { sessionExpired: true })
      if (response.type === 'opaqueredirect' || response.status === 401) throw expired()
      if (!response.ok) throw new Error('暂时无法确认登录状态，请重试')
      const text = await response.text()
      let data = text
      try { data = JSON.parse(text) } catch (_) { /* Legacy HTML expiry response. */ }
      if (validProfile(data)) return data
      if (ambiguousSessionResponse(data)) throw expired()
      throw new Error('暂时无法确认登录状态，请重试')
    }).catch(error => {
      if (error.sessionExpired) throw error
      throw new Error('暂时无法确认登录状态，请检查网络后重试')
    }).finally(() => { clearTimeout(timer); if (profileRevision === revision) profileCheck = null })
  }
  return profileCheck
}

async function inspectSession(response) {
  const config = response.config || {}
  if (!webAuth.browser || !config.webAppMode || config.skipSessionCheck || !ownApi(config) || config.sessionRevision !== webAuth.revision) return
  if (response.status === 401) {
    expireSession(config.sessionRevision)
    return
  }
  // App responses keep their previous interpretation. Do not turn business "1" into logout.
  if (!webAuth.session) return
  if (response.status === 403 || ambiguousSessionResponse(response.data)) {
    try { await checkWebSession() } catch (error) {
      if (error.sessionExpired) expireSession(config.sessionRevision)
    }
  }
}

request.interceptors.request.use(config => {
  config.webAppMode = webAuth.browser
  config.sessionRevision = webAuth.revision
  return config
})
request.interceptors.response.use(async response => {
  await inspectSession(response)
  if (response.config.webAppMode && !response.config.skipSessionCheck && ownApi(response.config) && webAuth.browser &&
      (webAuth.expired || response.config.sessionRevision !== webAuth.revision)) {
    throw Object.assign(new Error('登录状态已变化，请重新进入'), { sessionExpired: true })
  }
  return response.data
}, async error => {
  if (error.response) await inspectSession(error.response)
  else if (!axios.isCancel(error) && error.config && error.config.webAppMode && !error.config.skipSessionCheck && ownApi(error.config) && webAuth.browser && webAuth.session) {
    try { await checkWebSession() } catch (checkError) {
      if (checkError.sessionExpired) expireSession(error.config.sessionRevision)
    }
  }
  return Promise.reject(error)
})

const installer = {
  vm: {},
  install (Vue) {
    Vue.use(VueAxios, request)
  }
}

export default request

export {
  installer as VueAxios,
  request as axios
}
